import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  UserPlus, Pencil, Trash2, Search, Shield, Save, Check, X, Camera, Lock, 
  User, Mail, Calendar, Phone, Eye, EyeOff, Users, UserCheck, Wallet, 
  CheckCircle2, Sparkles, Filter, AlertCircle, ArrowRight, KeyRound, Layers, Briefcase
} from 'lucide-react';
import toast from 'react-hot-toast';
import userService from '../services/userService.js';
import api from '../services/api.js';
import { useAuth } from '../App.jsx';
import { Fn } from '../utils/rutValidation.js';

/**
 * Usuarios — CRUD de usuarios para administradores y super administradores.
 * Permite listar, crear (solo super_admin), editar (admin y super_admin) y eliminar.
 */

const ROLES = ['super_admin', 'admin', 'boss', 'staff'];

const FORM_VACIO = {
  name: '',
  email: '',
  password: '',
  role: 'staff',
  rut: '',
  phone: '',
  cargo: '',
  active: true,
  fecha_nacimiento: '',
  is_caja: false,
  can_edit_repertorios: false,
  can_assign_tramites: false,
  areas: []
};

const normalizeDateForInput = (dateVal) => {
  if (!dateVal) return '';
  const s = String(dateVal).trim();
  if (s.includes('/')) {
    const parts = s.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
  }
  return s.split('T')[0];
};

// ── Modal Crear/Editar Funcionario ──────────────────────────────────────────
function UserModal({ usuario, onClose, onSaved, areasDisponibles = [] }) {
  const { user: currentUserContext, hasRole } = useAuth();
  const isSuperAdmin = hasRole ? hasRole('super_admin') : (currentUserContext?.role === 'super_admin');

  const [form, setForm] = useState(() => {
    if (usuario) {
      return {
        name: usuario.name || '',
        email: usuario.email || '',
        password: '',
        role: usuario.role || 'staff',
        rut: usuario.rut || '',
        phone: usuario.phone || '',
        cargo: usuario.cargo || '',
        active: Boolean(usuario.active),
        is_caja: Boolean(usuario.is_caja),
        can_edit_repertorios: Boolean(usuario.can_edit_repertorios),
        can_assign_tramites: Boolean(usuario.can_assign_tramites),
        fecha_nacimiento: normalizeDateForInput(usuario.fecha_nacimiento),
        areas: Array.isArray(usuario.areas) ? usuario.areas.map(a => a.id) : []
      };
    }
    return { ...FORM_VACIO };
  });

  const [showPassword, setShowPassword] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(usuario?.avatar_url || null);
  const [guardando, setGuardando] = useState(false);

  // Atajo de teclado: Ctrl/Cmd + Enter para guardar y Esc para cerrar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [form, avatarFile, guardando]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAreaToggle = (areaId) => {
    setForm((p) => {
      const exists = p.areas.includes(areaId);
      const areas = exists ? p.areas.filter((id) => id !== areaId) : [...p.areas, areaId];
      return { ...p, areas };
    });
  };

  const handleSelectAllAreas = () => {
    setForm((p) => ({ ...p, areas: areasDisponibles.map((a) => a.id) }));
  };

  const handleClearAllAreas = () => {
    setForm((p) => ({ ...p, areas: [] }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede exceder 5 MB.');
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (guardando) return;

    // Validaciones preventivas en cliente
    if (!form.name?.trim()) {
      toast.error('Por favor ingresa el nombre completo del funcionario.');
      document.getElementById('input-user-name')?.focus();
      return;
    }

    if (!form.email?.trim()) {
      toast.error('Por favor ingresa el correo electrónico institucional.');
      document.getElementById('input-user-email')?.focus();
      return;
    }

    // Validación básica de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      toast.error('El formato del correo electrónico institucional no es válido.');
      document.getElementById('input-user-email')?.focus();
      return;
    }

    // Validación de RUT si fue provisto
    if (form.rut && form.rut.trim()) {
      if (!Fn.validaRut(form.rut.trim())) {
        toast.error('El RUT ingresado no es válido. Formato esperado: 12.345.678-9');
        document.getElementById('input-user-rut')?.focus();
        return;
      }
    }

    // Validación de contraseña
    if (!usuario && (!form.password || form.password.length < 8)) {
      toast.error('La contraseña inicial debe tener al menos 8 caracteres.');
      document.getElementById('input-user-password')?.focus();
      return;
    }

    if (usuario && form.password && form.password.trim().length > 0 && form.password.trim().length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres.');
      document.getElementById('input-user-password')?.focus();
      return;
    }

    setGuardando(true);
    try {
      const dataToSubmit = new FormData();
      dataToSubmit.append('name', form.name.trim());
      dataToSubmit.append('email', form.email.trim());
      dataToSubmit.append('role', form.role);
      dataToSubmit.append('active', form.active ? '1' : '0');
      dataToSubmit.append('is_caja', form.is_caja ? '1' : '0');
      dataToSubmit.append('can_edit_repertorios', form.can_edit_repertorios ? '1' : '0');
      dataToSubmit.append('can_assign_tramites', form.can_assign_tramites ? '1' : '0');

      dataToSubmit.append('rut', form.rut ? form.rut.trim() : '');
      dataToSubmit.append('phone', form.phone ? form.phone.trim() : '');
      dataToSubmit.append('cargo', form.cargo ? form.cargo.trim() : '');

      if (form.fecha_nacimiento) {
        dataToSubmit.append('fecha_nacimiento', normalizeDateForInput(form.fecha_nacimiento));
      } else {
        dataToSubmit.append('fecha_nacimiento', '');
      }

      if (form.password && form.password.trim().length >= 8) {
        dataToSubmit.append('password', form.password.trim());
      }

      if (avatarFile instanceof File) {
        dataToSubmit.append('avatar', avatarFile);
      }

      // Áreas
      if (form.areas && form.areas.length > 0) {
        form.areas.forEach((id) => dataToSubmit.append('areas[]', id));
      } else {
        dataToSubmit.append('areas', '');
      }

      const saved = usuario
        ? await userService.update(usuario.id, dataToSubmit)
        : await userService.create(dataToSubmit);

      toast.success(usuario ? 'Funcionario actualizado con éxito' : 'Funcionario creado con éxito');
      onSaved(saved);
    } catch (err) {
      console.error('Error al guardar usuario:', err);
      const msgs = err.response?.data?.errors;
      if (msgs) {
        Object.values(msgs).flat().forEach((m) => toast.error(m));
      } else {
        toast.error(err.response?.data?.message || 'Error al guardar los datos del funcionario.');
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={(e) => e.target === e.currentTarget && !guardando && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.7)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
        zIndex: 1000
      }}
    >
      <div 
        className="modal-content animate-fade-in" 
        style={{ 
          maxWidth: 720, 
          width: '100%',
          maxHeight: '92vh', 
          display: 'flex',
          flexDirection: 'column',
          padding: 0, 
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 25px 60px -15px rgba(0, 51, 102, 0.35)',
          background: '#ffffff',
          border: '1px solid rgba(226, 232, 240, 0.9)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Encabezado Moderno ────────────────────────────────────────── */}
        <div style={{ 
          padding: '1.25rem 1.75rem', 
          background: 'linear-gradient(135deg, #002244 0%, #003366 100%)', 
          color: '#ffffff',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ 
              width: 46, height: 46, borderRadius: '12px', 
              background: 'rgba(255, 255, 255, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              {usuario ? <User size={24} color="#ffffff" /> : <UserPlus size={24} color="#ffffff" />}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#ffffff', fontWeight: 800 }}>
                  {usuario ? `Editar: ${usuario.name}` : 'Crear Nuevo Funcionario'}
                </h3>
                {usuario && (
                  <span style={{
                    background: 'rgba(255,255,255,0.2)',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    textTransform: 'uppercase'
                  }}>
                    ID #{usuario.id}
                  </span>
                )}
              </div>
              <p style={{ margin: '3px 0 0', fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                {usuario ? 'Actualice las credenciales, áreas operativas y facultades del personal' : 'Complete el perfil, roles y facultades del nuevo funcionario'}
              </p>
            </div>
          </div>

          <button 
            type="button" 
            onClick={onClose} 
            disabled={guardando}
            title="Cerrar modal"
            style={{ 
              background: 'rgba(255, 255, 255, 0.12)', 
              border: '1px solid rgba(255, 255, 255, 0.2)', 
              width: 34, height: 34, borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#ffffff', transition: 'all 0.15s' 
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)'}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Cuerpo del Formulario con Scroll Interno Suave ───────────── */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ 
            padding: '1.5rem 1.75rem', 
            overflowY: 'auto', 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1.5rem',
            backgroundColor: '#f8fafc' 
          }}>
            
            {/* SECCIÓN 1: CREDENCIALES Y ACCESO */}
            <div style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.6rem' }}>
                <div style={{ background: '#eff6ff', padding: '6px', borderRadius: '8px', color: '#003366' }}>
                  <Lock size={16} />
                </div>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#003366', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  1. Credenciales y Acceso al Sistema
                </h4>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <User size={14} color="#64748b" /> Nombre Completo <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input 
                    id="input-user-name"
                    name="name" 
                    value={form.name} 
                    onChange={handleChange} 
                    placeholder="Ej: Juan Pérez Morales" 
                    required 
                    style={{ borderRadius: '10px', border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: '0.9rem' }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Mail size={14} color="#64748b" /> Correo Institucional <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input 
                    id="input-user-email"
                    name="email" 
                    type="email" 
                    value={form.email} 
                    onChange={handleChange} 
                    placeholder="usuario@cbr.cl" 
                    required 
                    style={{ borderRadius: '10px', border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: '0.9rem' }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Shield size={14} color="#64748b" /> Rol de Usuario <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select 
                    name="role" 
                    value={form.role} 
                    onChange={handleChange} 
                    disabled={!isSuperAdmin}
                    style={{ 
                      borderRadius: '10px', border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: '0.9rem',
                      background: isSuperAdmin ? '#fff' : '#f1f5f9',
                      cursor: isSuperAdmin ? 'pointer' : 'not-allowed',
                      fontWeight: 600, color: '#1e293b'
                    }}
                  >
                    {isSuperAdmin && <option value="super_admin">⭐ Super Admin (Control Total)</option>}
                    <option value="admin">🛡️ Admin (Administrador)</option>
                    <option value="boss">👔 Boss (Jefatura de Área)</option>
                    <option value="staff">💼 Staff (Funcionario Operativo)</option>
                  </select>
                  {!isSuperAdmin && (
                    <span style={{ fontSize: '0.73rem', color: '#64748b', display: 'block', marginTop: 4 }}>
                      🔒 Solo el Super Admin puede reasignar roles de sistema.
                    </span>
                  )}
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>RUT del Funcionario</span>
                    {form.rut && form.rut.trim().length > 0 && (
                      <span style={{ 
                        fontSize: '0.72rem', 
                        fontWeight: 700, 
                        color: Fn.validaRut(form.rut.trim()) ? '#16a34a' : '#dc2626',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3
                      }}>
                        {Fn.validaRut(form.rut.trim()) ? '✓ Válido' : '⚠️ Inválido'}
                      </span>
                    )}
                  </label>
                  <input 
                    id="input-user-rut"
                    name="rut" 
                    value={form.rut || ''} 
                    onChange={handleChange} 
                    placeholder="12.345.678-9" 
                    style={{ 
                      borderRadius: '10px', 
                      border: form.rut && form.rut.trim().length > 3
                        ? (Fn.validaRut(form.rut.trim()) ? '1.5px solid #16a34a' : '1.5px solid #f87171')
                        : '1px solid #cbd5e1', 
                      padding: '10px 12px', 
                      fontSize: '0.9rem' 
                    }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <KeyRound size={14} color="#64748b" />
                    {usuario ? 'Cambiar Contraseña (Opcional)' : 'Contraseña de Acceso *'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      id="input-user-password"
                      name="password" 
                      type={showPassword ? 'text' : 'password'} 
                      value={form.password} 
                      onChange={handleChange}
                      placeholder={usuario ? 'Dejar en blanco para mantener la clave actual' : 'Mínimo 8 caracteres'} 
                      required={!usuario} 
                      minLength={8} 
                      style={{ 
                        borderRadius: '10px', border: '1px solid #cbd5e1', 
                        padding: '10px 42px 10px 12px', fontSize: '0.9rem', width: '100%' 
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                      style={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b',
                        padding: 4, display: 'flex', alignItems: 'center'
                      }}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* SECCIÓN 2: PERFIL PERSONAL Y CONTACTO */}
            <div style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.6rem' }}>
                <div style={{ background: '#fef3c7', padding: '6px', borderRadius: '8px', color: '#b45309' }}>
                  <Calendar size={16} />
                </div>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#003366', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  2. Perfil y Contacto Institucional
                </h4>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', alignItems: 'center' }}>
                <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Briefcase size={14} color="#64748b" /> Cargo / Función Institucional
                  </label>
                  <input 
                    name="cargo" 
                    value={form.cargo || ''} 
                    onChange={handleChange} 
                    placeholder="Ej: Conservador Titular, Abogado Revisor, Oficial Primero, Archivero..." 
                    style={{ borderRadius: '10px', border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: '0.9rem' }}
                  />
                  <span style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3, display: 'block' }}>
                    Aparecerá en firmas oficiales, timbres, carátulas y visualizaciones del sistema.
                  </span>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Calendar size={14} color="#64748b" /> Fecha de Nacimiento
                  </label>
                  <input 
                    name="fecha_nacimiento" 
                    type="date" 
                    value={form.fecha_nacimiento} 
                    onChange={handleChange} 
                    style={{ borderRadius: '10px', border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: '0.9rem' }}
                  />
                  <span style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3, display: 'block' }}>
                    Permite destacar los cumpleaños en el panel de bienvenida.
                  </span>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Phone size={14} color="#64748b" /> Teléfono de Contacto
                  </label>
                  <input 
                    name="phone" 
                    value={form.phone || ''} 
                    onChange={handleChange} 
                    placeholder="+56 9 1234 5678" 
                    style={{ borderRadius: '10px', border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: '0.9rem' }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.84rem', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '0.5rem' }}>
                    Foto de Perfil del Funcionario
                  </label>
                  <div style={{ 
                    display: 'flex', alignItems: 'center', gap: '1.25rem',
                    background: '#f8fafc', padding: '1rem', borderRadius: '12px', border: '1px dashed #cbd5e1'
                  }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: '50%',
                      background: '#ffffff', border: '2px solid #003366',
                      overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 10px rgba(0, 51, 102, 0.15)', flexShrink: 0
                    }}>
                      {avatarPreview ? (
                        <img src={avatarPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '1.8rem', opacity: 0.5 }}>👤</span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label 
                          htmlFor="user-avatar-upload"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '7px 14px', borderRadius: '8px', background: '#ffffff',
                            border: '1px solid #cbd5e1', color: '#1e293b', fontSize: '0.82rem',
                            fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = '#003366'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                        >
                          <Camera size={15} color="#003366" /> Seleccionar Fotografía
                        </label>
                        {avatarPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setAvatarFile(null);
                              setAvatarPreview(null);
                            }}
                            style={{
                              padding: '7px 12px', borderRadius: '8px', background: '#fee2e2',
                              border: '1px solid #fecaca', color: '#dc2626', fontSize: '0.82rem',
                              fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4
                            }}
                          >
                            <X size={14} /> Quitar
                          </button>
                        )}
                      </div>
                      <input 
                        id="user-avatar-upload"
                        type="file" 
                        onChange={handleFileChange} 
                        accept="image/*" 
                        style={{ display: 'none' }} 
                      />
                      <span style={{ display: 'block', fontSize: '0.74rem', color: '#64748b', marginTop: 4 }}>
                        Recomendado: Formato cuadrado JPG, PNG o WebP (hasta 5 MB).
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECCIÓN 3: ÁREAS OPERATIVAS ASIGNADAS */}
            <div style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ background: '#f5f3ff', padding: '6px', borderRadius: '8px', color: '#7c3aed' }}>
                    <Layers size={16} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#003366', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      3. Áreas de Especialidad Asignadas
                    </h4>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={handleSelectAllAreas}
                    style={{
                      fontSize: '0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
                      padding: '3px 8px', borderRadius: '6px', color: '#475569', cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    Marcar todas
                  </button>
                  <button
                    type="button"
                    onClick={handleClearAllAreas}
                    style={{
                      fontSize: '0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
                      padding: '3px 8px', borderRadius: '6px', color: '#475569', cursor: 'pointer', fontWeight: 600
                    }}
                  >
                    Desmarcar
                  </button>
                </div>
              </div>

              <p style={{ margin: '0 0 0.85rem', fontSize: '0.82rem', color: '#64748b' }}>
                Haga clic para activar o desactivar los libros y materias asignados a este funcionario ({form.areas.length} seleccionada{form.areas.length === 1 ? '' : 's'}):
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {areasDisponibles.map((a) => {
                  const selected = form.areas.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      style={{
                        cursor: 'pointer',
                        border: selected ? '1.5px solid #003366' : '1px solid #cbd5e1',
                        background: selected ? '#003366' : '#ffffff',
                        color: selected ? '#ffffff' : '#334155',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.82rem',
                        fontWeight: selected ? 700 : 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        boxShadow: selected ? '0 2px 6px rgba(0, 51, 102, 0.25)' : 'none',
                        transition: 'all 0.15s ease'
                      }}
                      onClick={() => handleAreaToggle(a.id)}
                    >
                      <span>{selected ? '✓' : '+'}</span>
                      {a.nombre}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SECCIÓN 4: FACULTADES Y PERMISOS */}
            <div style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.6rem' }}>
                <div style={{ background: '#ecfdf5', padding: '6px', borderRadius: '8px', color: '#059669' }}>
                  <Shield size={16} />
                </div>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#003366', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  4. Facultades y Permisos del Funcionario
                </h4>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                
                {/* 1. Usuario Activo */}
                <label style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.9rem 1.1rem', borderRadius: '12px',
                  border: form.active ? '1.5px solid #10b981' : '1px solid #cbd5e1',
                  background: form.active ? '#f0fdf4' : '#ffffff', cursor: 'pointer', transition: 'all 0.15s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: form.active ? '#dcfce7' : '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: form.active ? '#15803d' : '#94a3b8', flexShrink: 0, marginTop: 2
                    }}>
                      <UserCheck size={20} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: form.active ? '#15803d' : '#1e293b' }}>
                        Cuenta de Usuario Activa
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                        Permite al funcionario iniciar sesión en el portal, recibir trámites y emitir certificados.
                      </div>
                    </div>
                  </div>
                  <input 
                    type="checkbox" 
                    name="active" 
                    checked={form.active} 
                    onChange={handleChange}
                    style={{ width: 20, height: 20, accentColor: '#10b981', cursor: 'pointer' }} 
                  />
                </label>

                {/* 2. Designar como Caja */}
                <label style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.9rem 1.1rem', borderRadius: '12px',
                  border: form.is_caja ? '1.5px solid #3b82f6' : '1px solid #cbd5e1',
                  background: form.is_caja ? '#eff6ff' : '#ffffff', cursor: 'pointer', transition: 'all 0.15s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: form.is_caja ? '#dbeafe' : '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: form.is_caja ? '#1d4ed8' : '#94a3b8', flexShrink: 0, marginTop: 2
                    }}>
                      <Wallet size={20} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: form.is_caja ? '#1d4ed8' : '#1e293b' }}>
                        Habilitar como Módulo de Caja (Tesorería)
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                        Autoriza a validar pagos de aranceles de los ciudadanos y timbrar boletas oficiales.
                      </div>
                    </div>
                  </div>
                  <input 
                    type="checkbox" 
                    name="is_caja" 
                    checked={form.is_caja} 
                    onChange={handleChange}
                    style={{ width: 20, height: 20, accentColor: '#3b82f6', cursor: 'pointer' }} 
                  />
                </label>

                {/* 3. Editar Repertorios */}
                <label style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.9rem 1.1rem', borderRadius: '12px',
                  border: form.can_edit_repertorios ? '1.5px solid #f97316' : '1px solid #cbd5e1',
                  background: form.can_edit_repertorios ? '#fff7ed' : '#ffffff', cursor: 'pointer', transition: 'all 0.15s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: form.can_edit_repertorios ? '#ffedd5' : '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: form.can_edit_repertorios ? '#c2410c' : '#94a3b8', flexShrink: 0, marginTop: 2
                    }}>
                      <Pencil size={20} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: form.can_edit_repertorios ? '#c2410c' : '#1e293b' }}>
                        Facultad para editar / corregir repertorios
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                        Permite modificar números de repertorio, carátula o año de inscripción fijados en trámites.
                      </div>
                    </div>
                  </div>
                  <input 
                    type="checkbox" 
                    name="can_edit_repertorios" 
                    checked={form.can_edit_repertorios} 
                    onChange={handleChange}
                    style={{ width: 20, height: 20, accentColor: '#f97316', cursor: 'pointer' }} 
                  />
                </label>

                {/* 4. Asignar y Reasignar Trámites */}
                <label style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.9rem 1.1rem', borderRadius: '12px',
                  border: form.can_assign_tramites ? '1.5px solid #a855f7' : '1px solid #cbd5e1',
                  background: form.can_assign_tramites ? '#faf5ff' : '#ffffff', cursor: 'pointer', transition: 'all 0.15s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: form.can_assign_tramites ? '#f3e8ff' : '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: form.can_assign_tramites ? '#7e22ce' : '#94a3b8', flexShrink: 0, marginTop: 2
                    }}>
                      <ArrowRight size={20} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: form.can_assign_tramites ? '#7e22ce' : '#1e293b' }}>
                        Facultad para derivar / reasignar trámites (Control Operativo)
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                        Permite reasignar solicitudes entre distintos funcionarios para agilizar el flujo de trabajo.
                      </div>
                    </div>
                  </div>
                  <input 
                    type="checkbox" 
                    name="can_assign_tramites" 
                    checked={form.can_assign_tramites} 
                    onChange={handleChange}
                    style={{ width: 20, height: 20, accentColor: '#a855f7', cursor: 'pointer' }} 
                  />
                </label>

              </div>
            </div>

          </div>

          {/* ── FOOTER FIJO Y PROMINENTE (UX/UI GUARDAR) ────────────────── */}
          <div style={{ 
            padding: '1.2rem 1.75rem', 
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', 
            borderTop: '1px solid #e2e8f0', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexShrink: 0,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600 }}>
                * Campos requeridos para el registro
              </span>
              <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                Tip: Presiona <kbd style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace' }}>Ctrl + Enter</kbd> para guardar rápido
              </span>
            </div>

            <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
              <button 
                type="button" 
                onClick={onClose} 
                disabled={guardando}
                style={{
                  padding: '11px 22px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => { if (!guardando) e.currentTarget.style.background = '#f1f5f9'; }}
                onMouseLeave={e => { if (!guardando) e.currentTarget.style.background = '#ffffff'; }}
              >
                Cancelar
              </button>

              <button 
                type="submit" 
                disabled={guardando} 
                id="btn-guardar-usuario"
                style={{
                  padding: '12px 28px',
                  borderRadius: '12px',
                  border: 'none',
                  background: guardando 
                    ? '#94a3b8' 
                    : 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  letterSpacing: '0.01em',
                  cursor: guardando ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  boxShadow: guardando 
                    ? 'none' 
                    : '0 6px 20px -4px rgba(0, 51, 102, 0.45), 0 2px 6px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={e => { 
                  if (!guardando) { 
                    e.currentTarget.style.transform = 'translateY(-2px)'; 
                    e.currentTarget.style.boxShadow = '0 10px 25px -4px rgba(0, 51, 102, 0.55), 0 4px 10px rgba(0, 0, 0, 0.12)'; 
                    e.currentTarget.style.background = 'linear-gradient(135deg, #002244 0%, #003d80 100%)';
                  } 
                }}
                onMouseLeave={e => { 
                  if (!guardando) { 
                    e.currentTarget.style.transform = 'translateY(0)'; 
                    e.currentTarget.style.boxShadow = '0 6px 20px -4px rgba(0, 51, 102, 0.45), 0 2px 6px rgba(0, 0, 0, 0.08)'; 
                    e.currentTarget.style.background = 'linear-gradient(135deg, #003366 0%, #004d99 100%)';
                  } 
                }}
                onMouseDown={e => {
                  if (!guardando) e.currentTarget.style.transform = 'scale(0.98)';
                }}
                onMouseUp={e => {
                  if (!guardando) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
              >
                {guardando ? (
                  <>
                    <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                    <span>Guardando información...</span>
                  </>
                ) : (
                  <>
                    <Save size={18} strokeWidth={2.4} />
                    <span>{usuario ? 'Guardar Cambios' : 'Crear Funcionario'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Página Principal: Gestión de Usuarios ─────────────────────────────────────
export default function Usuarios() {
  const { user: currentUserContext, hasRole } = useAuth();
  const isSuperAdmin = hasRole ? hasRole('super_admin') : (currentUserContext?.role === 'super_admin');
  const isAdmin = hasRole ? hasRole('admin') : (currentUserContext?.role === 'admin');

  const [usuarios, setUsuarios] = useState([]);
  const [paginacion, setPaginacion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [rolFiltro, setRolFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('todos'); // 'todos', 'activos', 'inactivos'
  const [pagina, setPagina] = useState(1);
  const [areas, setAreas] = useState([]);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [usuarioEditar, setUsuarioEditar] = useState(null);

  // Confirmación de eliminación
  const [eliminandoId, setEliminandoId] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const data = await userService.list({
        search: busqueda || undefined,
        role: rolFiltro || undefined,
        page: pagina,
      });
      setUsuarios(data.data || []);
      setPaginacion(data);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar la lista de usuarios.');
    } finally {
      setCargando(false);
    }
  }, [busqueda, rolFiltro, pagina]);

  const cargarAreas = async () => {
    try {
      const res = await api.get('/tramites/areas');
      setAreas(res.data || []);
    } catch (err) {
      console.error("Error cargando áreas:", err);
    }
  };

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarAreas(); }, []);

  const abrirCrear = () => { setUsuarioEditar(null); setModalAbierto(true); };
  const abrirEditar = (u) => { setUsuarioEditar(u); setModalAbierto(true); };
  const cerrarModal = () => { setModalAbierto(false); setUsuarioEditar(null); };

  const onSaved = () => { cerrarModal(); cargar(); };

  const eliminar = async (id) => {
    if (eliminandoId !== id) {
      setEliminandoId(id);
      return;
    }
    try {
      await userService.remove(id);
      toast.success('Usuario eliminado exitosamente.');
      setEliminandoId(null);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al eliminar el usuario.');
      setEliminandoId(null);
    }
  };

  const BADGE_ROL = {
    super_admin: { label: 'Super Admin', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
    admin: { label: 'Admin', bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
    boss: { label: 'Jefatura (Boss)', bg: '#f5f3ff', color: '#5b21b6', border: '#ddd6fe' },
    staff: { label: 'Funcionario (Staff)', bg: '#f8fafc', color: '#334155', border: '#e2e8f0' },
  };

  // Filtrado local adicional para estado (Activo/Inactivo)
  const usuariosFiltrados = useMemo(() => {
    if (estadoFiltro === 'activos') return usuarios.filter(u => u.active);
    if (estadoFiltro === 'inactivos') return usuarios.filter(u => !u.active);
    return usuarios;
  }, [usuarios, estadoFiltro]);

  // Métricas para tarjetas de estadísticas
  const stats = useMemo(() => {
    const total = paginacion?.total || usuarios.length;
    const activos = usuarios.filter(u => u.active).length;
    const cajeros = usuarios.filter(u => u.is_caja).length;
    const admins = usuarios.filter(u => u.role === 'admin' || u.role === 'super_admin').length;
    return { total, activos, cajeros, admins };
  }, [usuarios, paginacion]);

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px)', maxWidth: 1400, margin: '0 auto' }}>
      
      {/* ── Encabezado Principal ───────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.25rem', marginBottom: '2rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: '#003366', color: '#ffffff', padding: 8, borderRadius: 12 }}>
              <Users size={24} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 800, color: '#003366', letterSpacing: '-0.02em' }}>
                Gestión de Personal y Cuentas
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '0.88rem', color: '#64748b' }}>
                Administre los accesos, facultades operativas y áreas asignadas a los funcionarios del Conservador.
              </p>
            </div>
          </div>
        </div>

        {isSuperAdmin && (
          <button 
            className="btn btn-primary" 
            onClick={abrirCrear}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 12, fontWeight: 700, fontSize: '0.92rem',
              boxShadow: '0 4px 14px rgba(0, 51, 102, 0.25)'
            }}
          >
            <UserPlus size={18} /> Registrar Nuevo Funcionario
          </button>
        )}
      </div>

      {/* ── Tarjetas Métricas Ejecutivas ───────────────────────────── */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '1rem', 
        marginBottom: '1.75rem' 
      }}>
        <div style={{ background: '#ffffff', borderRadius: 16, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1d4ed8' }}>
            <Users size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.total}</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginTop: 4 }}>Total Funcionarios</div>
          </div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: 16, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
            <UserCheck size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.activos}</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginTop: 4 }}>Cuentas Activas</div>
          </div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: 16, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
            <Wallet size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.cajeros}</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginTop: 4 }}>Cajeros Habilitados</div>
          </div>
        </div>

        <div style={{ background: '#ffffff', borderRadius: 16, padding: '1.25rem', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
            <Shield size={24} />
          </div>
          <div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.admins}</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginTop: 4 }}>Administradores</div>
          </div>
        </div>
      </div>

      {/* ── Filtros y Búsqueda ─────────────────────────────────────── */}
      <div style={{ 
        background: '#ffffff', borderRadius: 16, padding: '1.25rem', 
        border: '1px solid #e2e8f0', marginBottom: '1.5rem', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
          {/* Input Buscador */}
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 240 }}>
            <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
              placeholder="Buscar por nombre, email o RUT..."
              style={{
                width: '100%', padding: '10px 38px 10px 40px', borderRadius: 10,
                border: '1px solid #cbd5e1', fontSize: '0.88rem'
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => { setBusqueda(''); setPagina(1); }}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: '#f1f5f9', border: 'none', borderRadius: '50%', width: 22, height: 22,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b'
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Selector de Rol */}
          <div style={{ minWidth: 170 }}>
            <select 
              value={rolFiltro} 
              onChange={(e) => { setRolFiltro(e.target.value); setPagina(1); }}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                border: '1px solid #cbd5e1', fontSize: '0.88rem', fontWeight: 600, color: '#334155'
              }}
            >
              <option value="">Todos los Roles</option>
              <option value="super_admin">⭐ Super Admin</option>
              <option value="admin">🛡️ Admin</option>
              <option value="boss">👔 Boss</option>
              <option value="staff">💼 Staff</option>
            </select>
          </div>

          {/* Filtro de Estado Rápido */}
          <div style={{ display: 'flex', background: '#f1f5f9', padding: 3, borderRadius: 10 }}>
            <button
              type="button"
              onClick={() => setEstadoFiltro('todos')}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none',
                background: estadoFiltro === 'todos' ? '#ffffff' : 'transparent',
                color: estadoFiltro === 'todos' ? '#003366' : '#64748b',
                fontWeight: estadoFiltro === 'todos' ? 700 : 500, fontSize: '0.82rem',
                cursor: 'pointer', boxShadow: estadoFiltro === 'todos' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              Todos
            </button>
            <button
              type="button"
              onClick={() => setEstadoFiltro('activos')}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none',
                background: estadoFiltro === 'activos' ? '#ffffff' : 'transparent',
                color: estadoFiltro === 'activos' ? '#15803d' : '#64748b',
                fontWeight: estadoFiltro === 'activos' ? 700 : 500, fontSize: '0.82rem',
                cursor: 'pointer', boxShadow: estadoFiltro === 'activos' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              Activos
            </button>
            <button
              type="button"
              onClick={() => setEstadoFiltro('inactivos')}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none',
                background: estadoFiltro === 'inactivos' ? '#ffffff' : 'transparent',
                color: estadoFiltro === 'inactivos' ? '#b91c1c' : '#64748b',
                fontWeight: estadoFiltro === 'inactivos' ? 700 : 500, fontSize: '0.82rem',
                cursor: 'pointer', boxShadow: estadoFiltro === 'inactivos' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              Inactivos
            </button>
          </div>
        </div>

        <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
          Mostrando <strong>{usuariosFiltrados.length}</strong> funcionarios
        </span>
      </div>

      {/* ── Tabla de Funcionarios ───────────────────────────────────── */}
      {cargando ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: '#fff', borderRadius: 16 }}>
          <span className="spinner spinner-lg" />
          <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.9rem' }}>Cargando funcionarios...</p>
        </div>
      ) : (
        <div className="animate-fade-in" style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
          <div className="table-wrapper" style={{ margin: 0 }}>
            <table>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem' }}>Funcionario</th>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem' }}>Contacto / RUT</th>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem' }}>Fecha Nac.</th>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem' }}>Rol y Facultades</th>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem' }}>Áreas Asignadas</th>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem' }}>Estado</th>
                  <th style={{ padding: '14px 18px', color: '#475569', fontWeight: 700, fontSize: '0.84rem', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '3.5rem' }}>
                      <AlertCircle size={40} style={{ opacity: 0.3, marginBottom: 10 }} /><br />
                      No se encontraron funcionarios con los filtros aplicados.
                    </td>
                  </tr>
                ) : usuariosFiltrados.map((u) => {
                  const roleStyle = BADGE_ROL[u.role] || BADGE_ROL.staff;
                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                      {/* Avatar y Nombre */}
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ position: 'relative' }}>
                            <div style={{
                              width: 38, height: 38, borderRadius: '50%',
                              background: u.avatar_url ? `url(${u.avatar_url}) center/cover` : 'linear-gradient(135deg, #003366, #005599)',
                              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.9rem', fontWeight: 700, flexShrink: 0,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                            }}>
                              {!u.avatar_url && (u.name ? u.name[0].toUpperCase() : 'U')}
                            </div>
                            <span style={{
                              position: 'absolute', bottom: -1, right: -1,
                              width: 10, height: 10, borderRadius: '50%',
                              background: u.active ? '#10b981' : '#94a3b8',
                              border: '2px solid #ffffff'
                            }} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>
                              {u.name}
                            </div>
                            {u.cargo && (
                              <div style={{ fontSize: '0.74rem', fontWeight: 600, color: '#003366', marginTop: 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <Briefcase size={12} color="#003366" /> {u.cargo}
                              </div>
                            )}
                            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Contacto y RUT */}
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                          {u.rut || '—'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {u.phone || 'Sin teléfono'}
                        </div>
                      </td>

                      {/* Fecha Nacimiento */}
                      <td style={{ padding: '12px 18px', fontSize: '0.85rem', color: '#475569' }}>
                        {u.fecha_nacimiento ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            🎂 {new Date(u.fecha_nacimiento + 'T12:00:00').toLocaleDateString('es-CL')}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>—</span>
                        )}
                      </td>

                      {/* Rol y Facultades */}
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          <span style={{
                            fontSize: '0.75rem', fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                            background: roleStyle.bg, color: roleStyle.color, border: `1px solid ${roleStyle.border}`,
                            display: 'inline-flex', alignItems: 'center', gap: 4
                          }}>
                            <Shield size={11} /> {roleStyle.label}
                          </span>
                          
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                            {u.is_caja && (
                              <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                                💰 CAJA
                              </span>
                            )}
                            {u.can_edit_repertorios && (
                              <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: '#ffedd5', color: '#c2410c', fontWeight: 600 }}>
                                ✏️ REPTOS
                              </span>
                            )}
                            {u.can_assign_tramites && (
                              <span style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, background: '#f3e8ff', color: '#7e22ce', fontWeight: 600 }}>
                                🎯 DERIVA
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Áreas Asignadas */}
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
                          {u.areas && u.areas.length > 0 ? (
                            u.areas.map(a => (
                              <span key={a.id} style={{
                                fontSize: '0.72rem', padding: '2px 8px', borderRadius: 6,
                                background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', fontWeight: 500
                              }}>
                                {a.nombre}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Sin áreas</span>
                          )}
                        </div>
                      </td>

                      {/* Estado */}
                      <td style={{ padding: '12px 18px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontSize: '0.78rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                          background: u.active ? '#dcfce7' : '#fee2e2',
                          color: u.active ? '#15803d' : '#b91c1c'
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.active ? '#16a34a' : '#dc2626' }} />
                          {u.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button
                            type="button"
                            onClick={() => abrirEditar(u)}
                            title="Editar Funcionario"
                            style={{
                              padding: '6px 12px', borderRadius: 8,
                              border: '1px solid #cbd5e1', background: '#ffffff',
                              color: '#003366', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                              fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.15s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#003366'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                          >
                            <Pencil size={14} /> Editar
                          </button>

                          {isSuperAdmin && (
                            <button
                              type="button"
                              onClick={() => eliminar(u.id)}
                              title={eliminandoId === u.id ? 'Haga clic de nuevo para confirmar eliminación' : 'Eliminar Funcionario'}
                              style={{
                                padding: '6px 10px', borderRadius: 8,
                                border: eliminandoId === u.id ? '1px solid #dc2626' : '1px solid #e2e8f0',
                                background: eliminandoId === u.id ? '#dc2626' : '#ffffff',
                                color: eliminandoId === u.id ? '#ffffff' : '#ef4444',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.15s'
                              }}
                              onMouseEnter={e => { if (eliminandoId !== u.id) e.currentTarget.style.background = '#fef2f2'; }}
                              onMouseLeave={e => { if (eliminandoId !== u.id) e.currentTarget.style.background = '#ffffff'; }}
                            >
                              <Trash2 size={14} />
                              {eliminandoId === u.id && <span>Confirmar</span>}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {paginacion && paginacion.last_page > 1 && (
            <div style={{ 
              padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
            }}>
              <span style={{ fontSize: '0.84rem', color: '#64748b' }}>
                Página <strong>{pagina}</strong> de <strong>{paginacion.last_page}</strong> ({paginacion.total} funcionarios en total)
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className="btn btn-secondary btn-sm" 
                  onClick={() => setPagina(p => p - 1)} 
                  disabled={pagina === 1}
                  style={{ borderRadius: 8 }}
                >
                  ← Anterior
                </button>
                <button 
                  className="btn btn-secondary btn-sm" 
                  onClick={() => setPagina(p => p + 1)} 
                  disabled={pagina === paginacion.last_page}
                  style={{ borderRadius: 8 }}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalAbierto && (
        <UserModal 
          usuario={usuarioEditar} 
          onClose={cerrarModal} 
          onSaved={onSaved} 
          areasDisponibles={areas}
        />
      )}
    </div>
  );
}
